import axios, { AxiosResponse } from 'axios'
import { Message, Modal } from '@arco-design/web-react'
import {
  BLUR_CODE,
  SUCCESS_CODE,
  CURRENCY_COOKIE,
  LANG_COOKIE,
  TOKEN_COOKIE,
  REGION_COOKIE,
  UNAUTHORIZED_CODE,
  NOT_VERIFIED,
  TIKTOK_SHOP_UNAUTHORIZED_CODE,
  TIKTOK_SHOP_EXPIRED_CODE,
  LOCAL_USERINFO_KEY,
  MULTIPLE_LOGIN_CODE,
  SECONDARY_CURRENCY_COOKIE
} from '@/utils/constants'
import { isSSR } from '@/utils/is'
import { getCookie } from 'cookies-next'
import { deleteToken } from '@/utils/checkLogin'
import { Res } from '@/types/api'
import type { StoreState } from '@/store'
import { actions as ssrActions } from '@/store/ssr'
import { Store } from 'redux'
import { getTiktokShopUrl } from '@/api/tiktok-shop'

export const backendCodeProcessor = <T>(
  res: AxiosResponse<Res<T>>,
  config?: {
    store?: Store
    memberModalClosable?: boolean
    defaultValue?: any
  }
) => {
  const { store, memberModalClosable = false, defaultValue } = config || {}

  if ([UNAUTHORIZED_CODE].includes(res.data.code)) {
    store?.dispatch(
      ssrActions.updateSsrStoreAction({
        showMemberModal: true,
        memberModalClosable
      })
    )

    return res?.data?.data || defaultValue || {}
  }

  if ([SUCCESS_CODE, BLUR_CODE].includes(res.data.code)) {
    return res.data.data
  }

  if ([NOT_VERIFIED].includes(res.data.code)) {
    return Promise.reject(res)
  }

  if ([TIKTOK_SHOP_UNAUTHORIZED_CODE, TIKTOK_SHOP_EXPIRED_CODE].includes(res.data.code)) {
    getTiktokShopUrl()
      .then((res) => {
        if (res.data.data.auth_url) {
          // 跳转到 TikTok 店铺授权链接进行授权
          window.location.href = res.data.data.auth_url
        }
      })
      .finally(() => {
        return Promise.reject(res)
      })
  }
  if (!isSSR && [MULTIPLE_LOGIN_CODE].includes(res.data.code)) {
    Modal.warning({
      title: `${res?.data?.msg}`,
      content: `${res?.data?.msg}` || 'Sorry, your account has been logged in on other devices. Please try again.',
      onOk: () => {
        deleteToken()
        window.location.href = '/login'
      },
      onCancel: () => {
        deleteToken()
      },
      afterClose: () => {
        deleteToken()
        window.location.href = '/login'
      }
    })
    return Promise.reject(res)
  }
  return Promise.reject(res)
}

export const parseBlurRes = <T>({
  data,
  error,
  loading
}: {
  data?: AxiosResponse<Res<T>>
  error?: Error
  loading: boolean
}) => {
  return {
    data: data?.data?.data || [],
    error,
    loading,
    blur: data?.data?.code === BLUR_CODE,
    rawData: data
  }
}

export const generateSsrCtx = (storeState: StoreState) => {
  const { lang, currency, token, region, ua, secondaryCurrency } = storeState.ssr
  const ssrCtx = { lang, currency, token, region, ua, secondaryCurrency }

  return ssrCtx
}

// 基础url
export const baseOrigin =
  process.env.NEXT_PUBLIC_BUILD_ENV === 'test' || process.env.NODE_ENV === 'development'
    ? 'https://test.echotik.live'
    : 'https://echotik.live'

export const baseURL = `${baseOrigin}/api/v1`

axios.defaults.baseURL = baseURL

axios.defaults.withCredentials = true

export const handle401 = () => {
  setTimeout(() => {
    deleteToken()
    window.location.href = window?.location?.href
      ? `/login?callback=${encodeURIComponent(window?.location?.href)}`
      : '/login'
  }, 100)
}

const handleNotVerified = () => {
  setTimeout(() => {
    window.location.href = '/not-verified'
  }, 100)
}

axios.interceptors.request.use(
  (config) => {
    const { ssrCtx } = config

    const lang = isSSR ? ssrCtx?.lang : getCookie(LANG_COOKIE)
    const currency = isSSR ? ssrCtx?.currency : getCookie(CURRENCY_COOKIE)
    const secondaryCurrency = isSSR ? ssrCtx?.secondaryCurrency : getCookie(SECONDARY_CURRENCY_COOKIE)
    const token = isSSR ? ssrCtx?.token : getCookie(TOKEN_COOKIE)
    const region = isSSR ? ssrCtx?.region : getCookie(REGION_COOKIE)
    const ua = isSSR ? ssrCtx?.ua : ''

    if (config.method === 'get') {
      config.params = {
        ...config.params
      }
      config.data = {}
    } else {
      // 如果是FormData对象，不做处理
      if (!(config.data instanceof FormData)) {
        config.data = {
          ...config.data
        }
      }
    }

    config.headers = {
      ...(isSSR ? { 'User-Agent': ua || '' } : {}),
      ...config.headers,
      Authorization: `Bearer ${token}`,
      'x-lang': lang as string,
      'x-currency': currency as string,
      'x-secondary-currency': secondaryCurrency as string,
      'x-region': region as string
    }

    return config
  },
  (error) => {
    isSSR || Message.error('request fail')
    return Promise.reject(error)
  }
)

axios.interceptors.response.use(
  (res) => {
    if (!res?.config?.skipCodeCheck && res?.data?.code > 0) {
      const code = res?.data?.code
      if (code === NOT_VERIFIED) {
        isSSR || handleNotVerified()
      }
      if (![BLUR_CODE, UNAUTHORIZED_CODE].includes(code)) {
        // 显示接口报错信息：http code == 200 & 业务 code > 0
        isSSR || Message.warning(res?.data?.msg)
      }
      if (BLUR_CODE === code && !isSSR) {
        if (window?.localStorage) {
          localStorage.removeItem(LOCAL_USERINFO_KEY)
        }
      }
    }
    return res
  },
  (err) => {
    const { status, data = {}, config = {} } = err?.response || {}
    const { ssrCtx } = config
    const lang = isSSR ? ssrCtx?.lang : getCookie(LANG_COOKIE)

    const defaultFailMsg =
      lang === 'zh-CN' ? '抱歉，网站暂不可用，请稍后再试' : `Sorry, we've got a problem, please try again later`

    const msg = data?.msg || defaultFailMsg

    if (axios.isCancel(err)) {
      // do noting
    } else if (status === 403) {
      isSSR || (window.location.href = '/403')
    } else if (status === 404) {
      isSSR || (window.location.href = '/404')
    } else if (status === 401) {
      isSSR || handle401()
    } else if (status === NOT_VERIFIED) {
      isSSR || handleNotVerified()
    } else if (status === 429) {
      isSSR || Message.error('请求过于频繁，请稍后再试!')
    } else if (status !== 200) {
      isSSR || Message.error(msg)
    } else {
      // status = 200
      if (data.code && data.code > 0) {
        // 显示接口报错信息：http code == 200 & 业务 code > 0
        isSSR || Message.warning(msg)
      }
    }

    return Promise.reject(err)
  }
)

export default axios
